import { Guide } from '../../types/guide';

const guide: Guide = {
  slug: 'how-to-convert-pdf-to-markdown',
  title: 'How to Convert PDF to Markdown (Free, No Upload)',
  description:
    'Step-by-step guide to converting PDF files to clean Markdown with headings, lists, and tables — free, private, and entirely in your browser.',
  category: 'Converter',
  publishedDate: '2026-08-06',
  affiliateCategory: 'general',
  body: `
Markdown is the format of choice for documentation sites, static site generators, README files, and anywhere you want plain text that still carries structure — headings, bold/italic text, lists, links, and tables. If you've got a PDF and need it as Markdown instead of a wall of unformatted text, here's how to do it without installing anything or uploading your file anywhere.

## Why convert a PDF to Markdown instead of plain text?

Plain text extraction throws away structure — headings, bullet points, and tables all collapse into the same flat paragraphs. Markdown preserves that structure using lightweight syntax (\`#\` for headings, \`-\` for lists, \`|\` for tables), so the result is both human-readable and ready to drop into a static site, a wiki, or an LLM prompt.

## Steps

1. **Open [pdf-to-text.com](https://pdf-to-text.com)** — no account, no install.
2. **Upload your PDF.** Drag it into the upload area or click to browse. Processing happens entirely in your browser; the file is never sent to a server.
3. **Switch the output toggle to "Markdown."** The converter re-parses the document and reconstructs headings, lists, bold/italic runs, and tables as best-effort Markdown.
4. **Review the preview.** Use the Preview/Raw toggle to check the rendered Markdown against the raw source, and the search box to jump to a specific section in long documents.
5. **Copy or download.** Use the copy icon for quick pastes, or the download button to save a \`.md\` file.

## A note on formatting fidelity

PDF is a layout format, not a structural one — it describes where text sits on a page, not whether that text is a heading or a table cell. The converter does its best to infer structure from font size, spacing, and layout, which works well for most documents but isn't perfect for complex, heavily designed PDFs. Simple, mostly-text documents convert cleanest.

## If your PDF is a scanned image

If the PDF was created by scanning a paper document, there's no embedded text layer to extract — the "page" is really just a picture. The converter will tell you this rather than silently returning nothing. OCR (optical character recognition) support for scanned PDFs is on the roadmap; see [How to Extract Text from a Scanned PDF](/guides/how-to-extract-text-from-a-scanned-pdf) for the current state.
`,
};

export default guide;
